import { useCallback, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Box, Button, Center, Flex, Popover, PopoverArrow, PopoverBody, PopoverContent,
  PopoverTrigger, Spinner, Text, VStack, useDisclosure, useToast,
} from '@chakra-ui/react';
import { AddIcon } from '@chakra-ui/icons';
import { LuCircleHelp, LuTarget } from 'react-icons/lu';
import { useTranslation } from 'react-i18next';
import { deleteGoal, updateGoal } from '../../api/endpointsBackend';
import { useDisplayCurrency } from '../../contexts/DisplayCurrencyContext';
import { usePreferences } from '../../contexts/PreferencesContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import useGoalsEnabled from '../../hooks/useGoalsEnabled';
import { useGoalsView } from '../../hooks/useGoalsView';
import { useResizablePanel } from '../../hooks/useResizablePanel';
import { useUrlSyncedId } from '../../hooks/useUrlSyncedId';
import { formatAmount } from '../../utils/goals';
import ConfirmationDialog from '../shared/ConfirmationDialog';
import DisplayCurrencySelector from '../shared/DisplayCurrencySelector';
import PageHeader from '../shared/PageHeader';
import PanelResizeHandle, { pagePanelOptions, resizablePanelWidth } from '../shared/PanelResizeHandle';
import GoalDetailPanel from './GoalDetailPanel';
import GoalIcon from './GoalIcon';
import GoalProgress from './GoalProgress';
import GoalsRollupCard from './GoalsRollupCard';
import GoalSuggestionCards from './GoalSuggestions';
import useIsPhone from '../../hooks/useIsPhone';
import PhoneGoals from '../phone/PhoneGoals';

const PANEL_CREATE_MODE = 'new';
const SECTIONS = ['active', 'ready', 'archived'];

/** The three rules that decide what a goal counts, in one popover. */
function HowGoalsWork() {
  const { t } = useTranslation('goals');
  const lines = t('empty.how', { returnObjects: true });

  return (
    <Popover placement="bottom" isLazy>
      <PopoverTrigger>
        <Button size="sm" variant="ghost" leftIcon={<Box as={LuCircleHelp} boxSize="14px" />} color="redesign.text.muted">
          {t('empty.howTitle')}
        </Button>
      </PopoverTrigger>
      <PopoverContent w="320px">
        <PopoverArrow />
        <PopoverBody>
          <VStack align="start" spacing={2}>
            {(Array.isArray(lines) ? lines : []).map((line) => (
              <Text key={line} fontSize="xs" color="redesign.text" textAlign="left">{line}</Text>
            ))}
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}

function GoalRow({ goal, isSelected, onClick }) {
  const summary = goal.summary;
  const amountColor = summary?.is_achieved
    ? 'redesign.amount.income'
    : (summary?.saved || 0) < 0 ? 'redesign.accent.red' : 'redesign.heading';

  return (
    <Flex
      align="center"
      gap={3}
      h="44px"
      px="10px"
      borderRadius="lg"
      cursor="pointer"
      bg={isSelected ? 'redesign.bg.accent' : 'transparent'}
      _hover={{ bg: isSelected ? 'redesign.bg.accent' : 'redesign.bg.hover' }}
      opacity={goal.status === 'archived' ? 0.5 : 1}
      onClick={() => onClick(goal)}
    >
      <GoalIcon goal={goal} />
      <Text flex={{ base: 1, md: '0 0 180px' }} minW={0} fontSize="sm" fontWeight={500} color="redesign.heading" isTruncated>
        {goal.name}
      </Text>
      <Box w="140px" flexShrink={0} display={{ base: 'none', sm: 'block' }}>
        <GoalProgress goal={goal} summary={summary} />
      </Box>
      <Text flex={{ base: '0 0 auto', md: 1 }} textAlign="right" whiteSpace="nowrap">
        {/* Goals can be in different currencies, so each row names the one it
            is read in — its own, or the display currency when converting. */}
        <Text as="span" fontSize="2xs" color="redesign.text.muted" mr={1}>
          {summary ? summary.currency : goal.currency}
        </Text>
        <Text as="span" fontSize="sm" fontWeight={600} color={amountColor}>
          {summary ? formatAmount(summary.saved) : '—'}
        </Text>
        <Text as="span" fontSize="xs" color="redesign.text.muted">
          {' / '}{formatAmount(summary ? summary.target : goal.target_amount)}
        </Text>
      </Text>
    </Flex>
  );
}

// The phone gets its own read-only page; the desktop body stays as it was.
export default function GoalsPage(props) {
  return useIsPhone() ? <PhoneGoals {...props} /> : <GoalsPageDesktop {...props} />;
}

function GoalsPageDesktop() {
  const { t } = useTranslation('goals');
  const { homeCurrency } = usePreferences();
  const goalsEnabled = useGoalsEnabled();
  const { displayCurrency } = useDisplayCurrency();
  const { can } = useWorkspace();
  const toast = useToast();
  const [selectedGoalId, setSelectedGoalId] = useUrlSyncedId('goal_id');
  const [goalToDelete, setGoalToDelete] = useState(null);
  // What a catalogue card fills into the create form.
  const [draft, setDraft] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [version, setVersion] = useState(0);
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  // The page's convert control changes the goals' figures, not only the card's.
  const { rows, loading, error, refetch } = useGoalsView({ currency: displayCurrency });

  const isPanelOpen = selectedGoalId !== null;
  const panelResize = useResizablePanel(isPanelOpen, pagePanelOptions(440));

  const sections = useMemo(
    () => SECTIONS
      .map((status) => ({ status, goals: rows.filter((g) => g.status === status) }))
      .filter((s) => s.goals.length > 0),
    [rows]
  );
  const rollupCurrency = displayCurrency || homeCurrency || rows[0]?.currency;
  const selectedRow = rows.find((g) => g.id === selectedGoalId) || null;

  const startCreate = (from = null) => {
    setDraft(from);
    setSelectedGoalId(PANEL_CREATE_MODE);
  };

  const handleChanged = useCallback(async (newGoalId) => {
    await refetch();
    setVersion((v) => v + 1);
    if (newGoalId) setSelectedGoalId(newGoalId);
  }, [refetch, setSelectedGoalId]);

  const handleStatusChange = async (goal, status) => {
    try {
      await updateGoal(goal.id, { status });
      toast({ title: t('toasts.successTitle'), description: t('toasts.updated', { name: goal.name }), status: 'success', duration: 3000, isClosable: true });
      handleChanged();
    } catch (err) {
      toast({ title: t('toasts.errorTitle'), description: t('toasts.saveFailed'), status: 'error', duration: 5000, isClosable: true });
    }
  };

  const handleDeleteRequest = (goal) => {
    setGoalToDelete(goal);
    onDeleteOpen();
  };

  const handleDeleteConfirm = async () => {
    if (!goalToDelete) return;
    setDeleteLoading(true);
    try {
      await deleteGoal(goalToDelete.id);
      toast({ title: t('toasts.successTitle'), description: t('toasts.deleted', { name: goalToDelete.name }), status: 'success', duration: 3000, isClosable: true });
      onDeleteClose();
      if (selectedGoalId === goalToDelete.id) setSelectedGoalId(null);
      setGoalToDelete(null);
      handleChanged();
    } catch (err) {
      toast({ title: t('toasts.errorTitle'), description: t('toasts.deleteFailed'), status: 'error', duration: 5000, isClosable: true });
    } finally {
      setDeleteLoading(false);
    }
  };

  // Goals is a per-user switch: with it off, the page does not exist for them.
  if (!goalsEnabled) return <Navigate to="/dashboard" replace />;

  return (
    <Flex ref={panelResize.containerRef} h={{ base: 'calc(100vh - 56px)', md: 'var(--mk-page-h)' }} overflow="hidden" position="relative">
      <Box flex={1} display="flex" flexDirection="column" minW={0}>
        <PageHeader title={t('title')}>
          <Flex align="center" gap={2}>
            <DisplayCurrencySelector />
            {can('goal.create') && (
              <Button size="sm" h="30px" colorScheme="blue" leftIcon={<AddIcon boxSize={2.5} />} onClick={() => startCreate()} aria-label={t('newAria')}>
                {t('addGoal')}
              </Button>
            )}
          </Flex>
        </PageHeader>

        <Box flex={1} overflowY="auto" overflowX="hidden" px={{ base: 3, md: 8 }} py={6}>
          {loading && rows.length === 0 ? (
            <Center py={20}>
              <VStack spacing={4}>
                <Spinner size="xl" color="redesign.text.muted" />
                <Text color="redesign.text">{t('loading')}</Text>
              </VStack>
            </Center>
          ) : error ? (
            <Center py={20}><Text color="redesign.accent.red">{t('loadError')}</Text></Center>
          ) : rows.length === 0 ? (
            <Center py={16}>
              <VStack spacing={4} maxW="680px" textAlign="center">
                <Box as={LuTarget} boxSize="32px" color="redesign.text.faint" />
                <Text fontSize="xl" fontWeight={600} color="redesign.heading">{t('empty.title')}</Text>
                <Text fontSize="sm" color="redesign.text.muted">{t('empty.subtitle')}</Text>
                <Flex gap={2} align="center">
                  {can('goal.create') && (
                    <Button leftIcon={<AddIcon />} colorScheme="blue" size="sm" onClick={() => startCreate()}>
                      {t('empty.createButton')}
                    </Button>
                  )}
                  <HowGoalsWork />
                </Flex>
                {can('goal.create') && (
                  <Box pt={4} w="100%">
                    <Text textStyle="microLabel" mb={3}>
                      {t('empty.suggestedTitle')}
                    </Text>
                    <GoalSuggestionCards onPick={startCreate} />
                  </Box>
                )}
              </VStack>
            </Center>
          ) : (
            <>
              <GoalsRollupCard currency={rollupCurrency} refreshKey={version} />
              {sections.map((section) => (
                <Box key={section.status} mb={6}>
                  <Flex align="center" gap={2} mb={2}>
                    <Text textStyle="microLabel">
                      {t(`sections.${section.status}`)}
                    </Text>
                    <Text fontSize="xs" color="redesign.text.muted">{section.goals.length}</Text>
                    <Box flex={1} h="1px" bg="redesign.border" />
                  </Flex>
                  <VStack spacing="2px" align="stretch">
                    {section.goals.map((goal) => (
                      <GoalRow
                        key={goal.id}
                        goal={goal}
                        isSelected={selectedGoalId === goal.id}
                        onClick={(g) => setSelectedGoalId(g.id)}
                      />
                    ))}
                  </VStack>
                </Box>
              ))}
            </>
          )}
        </Box>
      </Box>

      <Box
        display={{ base: isPanelOpen ? 'block' : 'none', md: 'block' }}
        position={{ base: 'fixed', md: 'relative' }}
        right={{ base: 0, md: 'auto' }}
        top={{ base: 0, md: 'auto' }}
        bottom={{ base: 0, md: 'auto' }}
        left={{ base: 0, md: 'auto' }}
        w={{ base: '100%', md: resizablePanelWidth('44%', 440), lg: resizablePanelWidth('42%', 440) }}
        h={{ base: '100dvh', md: '100%' }}
        bg="redesign.bg.surface"
        borderLeft="1px solid"
        borderLeftColor="redesign.border"
        boxShadow={{ base: '-2px 0 8px rgba(0,0,0,0.15)', md: 'none' }}
        zIndex={{ base: 200, md: 'auto' }}
      >
        <PanelResizeHandle resize={panelResize} />
        {isPanelOpen ? (
          <GoalDetailPanel
            goalId={selectedGoalId === PANEL_CREATE_MODE ? null : selectedGoalId}
            draft={draft}
            row={selectedGoalId === PANEL_CREATE_MODE ? null : selectedRow}
            loading={loading}
            onClose={() => setSelectedGoalId(null)}
            onChanged={handleChanged}
            onDelete={can('goal.delete') ? handleDeleteRequest : undefined}
            onStatusChange={handleStatusChange}
            canUpdate={can('goal.update')}
          />
        ) : (
          <Flex direction="column" align="center" justify="center" gap={3} h="100%" bg="redesign.bg.page" color="redesign.text.faint">
            <Box as={LuTarget} boxSize="32px" />
            <Text fontSize="sm" color="redesign.text.muted">{t('selectToView')}</Text>
          </Flex>
        )}
      </Box>

      <ConfirmationDialog
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        onConfirm={handleDeleteConfirm}
        title={t('deleteDialog.title')}
        message={goalToDelete ? t('deleteDialog.message', { name: goalToDelete.name }) : ''}
        confirmText={t('deleteDialog.confirm')}
        confirmColorScheme="red"
        isLoading={deleteLoading}
      />
    </Flex>
  );
}
